import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Building, ExternalLink, UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import { ROLE_LABELS, formatDate } from "@/lib/labels";
import { notifyCatalogChanged } from "@/context/CatalogContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  EmptyState,
  LoadingState,
  PageHeader,
  ReasonDialog,
  StatePill,
  UnsavedBar,
  apiErrorMessage,
} from "@/components/admin/AdminUI";

const CATEGORY_TITLES = { operacional: "Operação", estrutural: "Estrutura", configuracao: "Configurações" };

function CompanyData({ client, onSaved }) {
  const [form, setForm] = useState(client);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(client), [client]);
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const dirty = ["name", "code", "cnpj", "contact", "phone", "email"].some((f) => (form[f] || "") !== (client[f] || ""));
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${client.id}`, {
        name: form.name, code: form.code || "", cnpj: form.cnpj || "", contact: form.contact || "",
        phone: form.phone || "", email: form.email || "", responsibles: client.responsibles || [],
        company_id: client.company_id || null,
      });
      toast.success("Dados da empresa salvos.");
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-4 rounded-xl border border-border bg-card p-4" data-testid="company-data-tab">
      <div className="grid gap-3 sm:grid-cols-2">
        {[
          ["name", "Razão social / nome"], ["code", "Sigla"], ["cnpj", "CNPJ"],
          ["contact", "Contato"], ["phone", "Telefone"], ["email", "E-mail"],
        ].map(([field, label]) => (
          <div key={field} className="space-y-1.5">
            <Label htmlFor={`company-${field}`}>{label}</Label>
            <Input id={`company-${field}`} value={form[field] || ""} onChange={set(field)} data-testid={`company-field-${field}`} />
          </div>
        ))}
      </div>
      <UnsavedBar count={dirty ? 1 : 0} saving={saving} onSave={save} onDiscard={() => setForm(client)} testId="company-data-unsaved" />
    </div>
  );
}

function CompanyContract({ clientId, canManage }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin-center/tenants/${clientId}/contract`).then(({ data: payload }) => {
      setData(payload);
      setDraft({});
    }).catch((err) => toast.error(apiErrorMessage(err)));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const value = (item) => (item.key in draft ? draft[item.key] : item.contracted);
  const changes = Object.keys(draft).length;

  const save = async () => {
    setSaving(true);
    try {
      const entries = {};
      const complementary = {};
      for (const [key, enabled] of Object.entries(draft)) {
        if (data.complementary.some((c) => c.key === key)) complementary[key] = enabled;
        else entries[key] = enabled;
      }
      const { data: payload } = await api.put(`/admin-center/tenants/${clientId}/contract`, { entries, complementary });
      setData(payload);
      setDraft({});
      notifyCatalogChanged();
      toast.success("Contrato de módulos salvo e aplicado imediatamente.");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const groups = useMemo(() => {
    if (!data) return [];
    return Object.entries(CATEGORY_TITLES).map(([category, title]) => ({
      title,
      items: data.entries.filter((item) => item.category === category),
    })).filter((group) => group.items.length);
  }, [data]);

  if (!data) return <LoadingState />;
  const toggle = (item, enabled) =>
    setDraft((current) => {
      const next = { ...current };
      if (enabled === item.contracted) delete next[item.key];
      else next[item.key] = enabled;
      return next;
    });

  return (
    <div className="space-y-4" data-testid="company-contract-tab">
      <p className="text-sm text-muted-foreground">
        O contrato é o <strong>teto</strong> da empresa: nenhum usuário recebe módulo que não esteja contratado.
        Usuários recebem os módulos contratados conforme o perfil e os ajustes individuais.
      </p>
      {groups.map((group) => (
        <div key={group.title} className="rounded-xl border border-border bg-card">
          <h3 className="border-b border-border px-4 py-2.5 text-sm font-semibold">{group.title}</h3>
          <ul className="divide-y divide-border/60">
            {group.items.map((item) => {
              const on = value(item);
              const blockedByParent = item.depends_on.some((dep) => {
                const parent = data.entries.find((e) => e.key === dep);
                return parent && !value(parent);
              });
              return (
                <li key={item.key} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{item.label}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {item.contract_modules.join(", ")}
                      {item.inherited && " · herdado do módulo principal até ser definido"}
                      {item.partial && " · contratado parcialmente"}
                      {on && blockedByParent && " · inativo: depende de um módulo não contratado"}
                    </p>
                  </div>
                  <Switch
                    checked={on}
                    onCheckedChange={(checked) => toggle(item, checked)}
                    disabled={!canManage}
                    aria-label={`Contratar ${item.label}`}
                    data-testid={`contract-toggle-${item.key}`}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      <div className="rounded-xl border border-border bg-card">
        <h3 className="border-b border-border px-4 py-2.5 text-sm font-semibold">Recursos complementares</h3>
        <ul className="divide-y divide-border/60">
          {data.complementary.map((item) => (
            <li key={item.key} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <p className="text-sm">{item.label}</p>
              <Switch
                checked={item.key in draft ? draft[item.key] : item.contracted}
                onCheckedChange={(checked) => toggle(item, checked)}
                disabled={!canManage}
                aria-label={`Contratar ${item.label}`}
                data-testid={`contract-toggle-${item.key}`}
              />
            </li>
          ))}
        </ul>
      </div>
      <UnsavedBar count={changes} saving={saving} onSave={save} onDiscard={() => setDraft({})} testId="contract-unsaved" />
    </div>
  );
}

function CompanyUnits({ clientId }) {
  const [units, setUnits] = useState(null);
  useEffect(() => {
    api.get("/units", { params: { client_id: clientId } }).then(({ data }) => setUnits(Array.isArray(data) ? data : data.items || [])).catch(() => setUnits([]));
  }, [clientId]);
  return (
    <div className="space-y-3 rounded-xl border border-border bg-card p-4" data-testid="company-units-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Unidades, áreas, subáreas, responsáveis e dispositivos são mantidos na Central de Clientes.</p>
        <Button asChild variant="outline" size="sm">
          <Link to={`/central-clientes/${clientId}`} data-testid="company-manage-structure">
            Gerenciar estrutura <ExternalLink size={13} className="ml-1" />
          </Link>
        </Button>
      </div>
      {units === null ? <LoadingState /> : units.length === 0 ? (
        <EmptyState title="Nenhuma unidade cadastrada" />
      ) : (
        <ul className="divide-y divide-border/60">
          {units.map((unit) => (
            <li key={unit.id} className="py-2 text-sm">{unit.name}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CompanyUsers({ clientId }) {
  const [users, setUsers] = useState(null);
  useEffect(() => {
    api.get("/users").then(({ data }) => {
      const list = Array.isArray(data) ? data : data.items || [];
      setUsers(list.filter((u) => [...(u.client_ids || []), u.client_id, ...((u.scope || {}).client_ids || [])].includes(clientId)));
    }).catch(() => setUsers([]));
  }, [clientId]);
  return (
    <div className="space-y-3 rounded-xl border border-border bg-card p-4" data-testid="company-users-tab">
      <div className="flex justify-end">
        <Button asChild size="sm">
          <Link to={`/admin/usuarios?novo=1&empresa=${clientId}`} data-testid="company-new-user">
            <UserPlus size={14} className="mr-1" /> Novo usuário nesta empresa
          </Link>
        </Button>
      </div>
      {users === null ? <LoadingState /> : users.length === 0 ? (
        <EmptyState title="Nenhum usuário vinculado" />
      ) : (
        <ul className="divide-y divide-border/60">
          {users.map((u) => (
            <li key={u.id} className="flex items-center justify-between py-2 text-sm">
              <Link to={`/admin/usuarios/${u.id}`} className="font-medium hover:underline">{u.name}</Link>
              <span className="text-xs text-muted-foreground">{ROLE_LABELS[u.role] || u.role}{u.active === false ? " · inativo" : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function EntityHistory({ entityId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/audit", { params: { entity_id: entityId, limit: 50 } })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  }, [entityId]);
  if (items === null) return <LoadingState />;
  if (!items.length) return <EmptyState title="Sem registros de auditoria" />;
  return (
    <ul className="divide-y divide-border/60 rounded-xl border border-border bg-card" data-testid="entity-history">
      {items.map((item) => (
        <li key={item.id} className="px-4 py-2.5 text-xs">
          <span className="text-muted-foreground">{formatDate(item.created_at)}</span> · <strong>{item.user_name}</strong> · {item.detail || item.action}
        </li>
      ))}
    </ul>
  );
}

export default function AdminCompanyDetail() {
  const { clientId } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [statusDialog, setStatusDialog] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin-center/tenants/${clientId}`).then(({ data: payload }) => setData(payload)).catch((err) => {
      toast.error(apiErrorMessage(err));
      setData({ error: true });
    });
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <LoadingState />;
  if (data.error) return <EmptyState title="Empresa não encontrada" />;
  const { client, stats } = data;
  const tab = params.get("tab") || "dados";

  const changeStatus = async (reason) => {
    try {
      await api.patch(`/admin-center/tenants/${clientId}/status`, { active: !client.active, reason });
      toast.success(client.active ? "Empresa suspensa. Os dados foram preservados." : "Empresa reativada.");
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
      throw err;
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-company-detail">
      <Link to="/admin/empresas" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft size={13} /> Empresas
      </Link>
      <PageHeader
        icon={Building}
        title={client.name}
        description={`${stats.units} unidade(s) · ${stats.users} usuário(s)${client.cnpj ? ` · CNPJ ${client.cnpj}` : ""}`}
        actions={
          <>
            {client.active ? <StatePill tone="success" testId="company-status">Ativa</StatePill> : <StatePill tone="danger" testId="company-status">Suspensa</StatePill>}
            {data.can_manage && (
              <Button variant={client.active ? "outline" : "default"} size="sm" onClick={() => setStatusDialog(true)} data-testid="company-status-btn">
                {client.active ? "Suspender acesso" : "Reativar"}
              </Button>
            )}
          </>
        }
      />
      {!client.active && client.suspended_reason && (
        <p className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs">
          Suspensa em {formatDate(client.suspended_at)} — {client.suspended_reason}
        </p>
      )}
      <Tabs value={tab} onValueChange={(value) => setParams({ tab: value })}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="dados" data-testid="company-tab-dados">Dados</TabsTrigger>
          <TabsTrigger value="contrato" data-testid="company-tab-contrato">Contrato de módulos</TabsTrigger>
          <TabsTrigger value="unidades" data-testid="company-tab-unidades">Unidades</TabsTrigger>
          <TabsTrigger value="usuarios" data-testid="company-tab-usuarios">Usuários</TabsTrigger>
          <TabsTrigger value="historico" data-testid="company-tab-historico">Histórico</TabsTrigger>
        </TabsList>
        <TabsContent value="dados"><CompanyData client={client} onSaved={load} /></TabsContent>
        <TabsContent value="contrato"><CompanyContract clientId={clientId} canManage={data.can_manage} /></TabsContent>
        <TabsContent value="unidades"><CompanyUnits clientId={clientId} /></TabsContent>
        <TabsContent value="usuarios"><CompanyUsers clientId={clientId} /></TabsContent>
        <TabsContent value="historico"><EntityHistory entityId={clientId} /></TabsContent>
      </Tabs>
      <ReasonDialog
        open={statusDialog}
        onOpenChange={setStatusDialog}
        title={client.active ? "Suspender acesso da empresa" : "Reativar empresa"}
        description={
          client.active
            ? "Os usuários desta empresa deixam de acessar o sistema. Nenhum dado é excluído."
            : "Os usuários voltam a acessar conforme o contrato e os perfis."
        }
        confirmLabel={client.active ? "Suspender" : "Reativar"}
        destructive={client.active}
        requireReason={client.active}
        onConfirm={changeStatus}
        testId="company-status-dialog"
      />
    </div>
  );
}
